<template>
  <div class="row main-content">
    <div class="col">
      <h1 class="page-title font-custom text-uppercase mt-3">
        {{ $t('title.clearing.list') }}
      </h1>

      <div class="card rounded-0 mb-4">
        <form class="card-body">
          <div class="row mb-2">
            <!-- Distributor -->
            <div class="col-12 col-sm-6 col-xl-3">
              <label class="control-label fw-bold mb-1">
                {{ $t('label.clearing.selectedDistributorRef') }}
              </label>
              <select
                v-model="criterias.selectedDistributorRef"
                :disabled="isDistributorLocked"
                :class="status(v$.criterias.selectedDistributorRef)"
                name="distributor"
                class="form-control rounded-0 form-select"
                :data-placeholder="$t('label.clearing.selectedDistributorRef')"
              >
                <option
                  value
                  selected
                  disabled
                  hidden
                >
                  {{ $t('label.clearing.selectedDistributorRef') }}
                </option>
                <option
                  v-for="distributor in distributors"
                  :key="distributor.id"
                  :value="distributor"
                >
                  {{ distributor }}
                </option>
              </select>
              <p
                v-if="validationError"
                class="error"
              >
                <span v-if="v$.criterias.selectedDistributorRef.oneRequired.$invalid">{{ $t('error.COMPENSATION_MISSING_MANDATORY_CRITERIA') }}</span>
              </p>
            </div>

            <!-- Réseau carte -->
            <div class="col-12 col-sm-6 col-xl-3">
              <label class="control-label fw-bold mb-1">
                {{ $t('label.clearing.selectedScheme') }}
              </label>
              <select
                v-model="criterias.selectedScheme"
                :class="status(v$.criterias.selectedScheme)"
                name="scheme"
                class="form-control rounded-0 form-select"
                :data-placeholder="$t('label.clearing.selectedScheme')"
              >
                <option value />
                <option
                  v-for="type in schemes"
                  :key="type.id"
                  :value="type.value"
                >
                  {{ type.text }}
                </option>
              </select>
            </div>

            <!-- N° Porteur -->
            <div class="col-12 col-sm-6 col-xl-3">
              <label class="control-label fw-bold mb-1">
                {{ $t('label.clearing.selectedPan') }}
              </label>
              <input
                v-model="criterias.selectedPan"
                :class="status(v$.criterias.selectedPan)"
                type="text"
                class="form-control rounded-0"
                :placeholder="$t('label.clearing.selectedPan')"
              >
              <p
                v-if="validationError"
                class="error"
              >
                <span v-if="v$.criterias.selectedPan.oneRequired.$invalid">{{ $t('error.COMPENSATION_MISSING_MANDATORY_CRITERIA') }}</span>
                <span v-else-if="v$.criterias.selectedPan.$invalid">{{ $t('error.VALIDATION_FORMAT') }}</span>
              </p>
            </div>

            <!-- Identifiant de la carte -->
            <div class="col-12 col-sm-6 col-xl-3">
              <label class="control-label fw-bold mb-1">
                {{ $t('label.clearing.selectedCardId') }}
              </label>
              <input
                v-model="criterias.selectedCardId"
                :class="status(v$.criterias.selectedCardId)"
                type="text"
                class="form-control rounded-0"
                :placeholder="$t('label.clearing.selectedCardId')"
              >
              <p
                v-if="validationError"
                class="error"
              >
                <span v-if="v$.criterias.selectedCardId.oneRequired.$invalid">{{ $t('error.COMPENSATION_MISSING_MANDATORY_CRITERIA') }}</span>
                <span v-else-if="v$.criterias.selectedCardId.$invalid">{{ $t('error.VALIDATION_FORMAT') }}</span>
              </p>
            </div>
          </div>

          <div class="row mb-2">
            <!-- Identifiant transaction -->
            <div class="col-12 col-sm-6 col-xl-3">
              <label class="control-label fw-bold mb-1">
                {{ $t('label.clearing.selectedIdOperation') }}
              </label>
              <input
                v-model="criterias.selectedIdOperation"
                :class="status(v$.criterias.selectedIdOperation)"
                type="text"
                class="form-control rounded-0"
                :placeholder="$t('label.clearing.selectedIdOperation')"
              >
              <p
                v-if="validationError"
                class="error"
              >
                <span v-if="v$.criterias.selectedIdOperation.oneRequired.$invalid">{{ $t('error.COMPENSATION_MISSING_MANDATORY_CRITERIA') }}</span>
                <span v-else-if="v$.criterias.selectedIdOperation.$invalid">{{ $t('error.VALIDATION_FORMAT') }}</span>
              </p>
            </div>

            <!-- N° Autorisation -->
            <div class="col-12 col-sm-6 col-xl-3">
              <label class="control-label fw-bold mb-1">
                {{ $t('label.clearing.selectedNumAut') }}
              </label>
              <input
                v-model="criterias.selectedNumAut"
                :class="status(v$.criterias.selectedNumAut)"
                type="text"
                class="form-control rounded-0"
                :placeholder="$t('label.clearing.selectedNumAut')"
              >
            </div>

            <!-- Catégorie transaction -->
            <div class="col-12 col-sm-6 col-xl-3">
              <label class="control-label fw-bold mb-1">
                {{ $t('label.clearing.selectedClasseTransaction') }}
              </label>
              <select
                v-model="criterias.selectedClasseTransaction"
                :class="status(v$.criterias.selectedClasseTransaction)"
                name="classeTransaction"
                class="form-control rounded-0 form-select"
                :data-placeholder="$t('label.clearing.selectedClasseTransaction')"
              >
                <option
                  value
                  selected
                  disabled
                  hidden
                >
                  {{ $t('label.clearing.selectedClasseTransaction') }}
                </option>
                <option value />
                <option
                  v-for="type in transactionClasses"
                  :key="type.id"
                  :value="type.value"
                >
                  {{ type.text }}
                </option>
              </select>
            </div>

            <!-- Statut technique -->
            <div class="col-12 col-sm-6 col-xl-3">
              <label class="control-label fw-bold mb-1">
                {{ $t('label.clearing.selectedTransactionStatus') }}
              </label>
              <select
                v-model="criterias.selectedTransactionStatus"
                :class="status(v$.criterias.selectedTransactionStatus)"
                name="technicalStatus"
                class="form-control rounded-0 form-select"
                :data-placeholder="$t('label.clearing.selectedTransactionStatus')"
              >
                <option
                  value
                  selected
                  disabled
                  hidden
                >
                  {{ $t('label.clearing.selectedTransactionStatus') }}
                </option>
                <option value />
                <option
                  v-for="type in technicalStatuses"
                  :key="type.id"
                  :value="type"
                >
                  {{ type }}
                </option>
              </select>
            </div>
          </div>

          <div class="row mb-2">
            <!-- Start Date Transaction -->
            <div class="col-12 col-sm-6 col-xl-3">
              <label class="control-label fw-bold mb-1">
                {{ $t('label.clearing.selectedDateTransactionBegin') }}
              </label>
              <input
                v-model="criterias.selectedDateTransactionBegin"
                :class="status(v$.criterias.selectedDateTransactionBegin)"
                type="date"
                class="form-control rounded-0"
                placeholder="yyyy-MM-dd"
              >
              <p
                v-if="validationError"
                class="error"
              >
                <span v-if="v$.criterias.selectedDateTransactionBegin.required.$invalid">{{ $t('error.COMPENSATION_DATE_MANDATORY_CRITERIA') }}</span>
                <span v-else-if="v$.criterias.selectedDateTransactionBegin.rangeMax.$invalid">{{ $t('error.COMPENSATION_DATE_TRANSACTION_RANGE') }}</span>
              </p>
            </div>

            <!-- Transation Start Time -->
            <div class="col-12 col-sm-6 col-xl-3">
              <label class="control-label fw-bold mb-1">
                {{ $t('label.clearing.selectedTimeTransactionBegin') }}
              </label>
              <input
                v-model="criterias.selectedTimeTransactionBegin"
                :class="status(v$.criterias.selectedTimeTransactionBegin)"
                type="time"
                step="1"
                class="form-control rounded-0"
                placeholder="HH:mm:ss"
              >
              <p
                v-if="validationError"
                class="error"
              >
                <span v-if="v$.criterias.selectedTimeTransactionBegin.$invalid">{{ $t('error.MANDATORY_CRITERIA') }}</span>
              </p>
            </div>

            <!-- Transation End Date -->
            <div class="col-12 col-sm-6 col-xl-3">
              <label class="control-label fw-bold mb-1">
                {{ $t('label.clearing.selectedDateTransactionEnd') }}
              </label>
              <input
                v-model="criterias.selectedDateTransactionEnd"
                :class="status(v$.criterias.selectedDateTransactionEnd)"
                type="date"
                class="form-control rounded-0"
                placeholder="yyyy-MM-dd"
              >
              <p
                v-if="validationError"
                class="error"
              >
                <span v-if="v$.criterias.selectedDateTransactionEnd.required.$invalid">{{ $t('error.MANDATORY_CRITERIA') }}</span>
                <span v-else-if="v$.criterias.selectedDateTransactionEnd.rangeMax.$invalid">{{ $t('error.COMPENSATION_DATE_TRANSACTION_RANGE') }}</span>
              </p>
            </div>

            <!-- Transation End Time -->
            <div class="col-12 col-sm-6 col-xl-3">
              <label class="control-label fw-bold mb-1">
                {{ $t('label.clearing.selectedTimeTransactionEnd') }}
              </label>
              <input
                v-model="criterias.selectedTimeTransactionEnd"
                :class="status(v$.criterias.selectedTimeTransactionEnd)"
                type="time"
                step="1"
                class="form-control rounded-0"
                placeholder="HH:mm:ss"
              >
              <p
                v-if="validationError"
                class="error"
              >
                <span v-if="v$.criterias.selectedTimeTransactionEnd.$invalid">{{ $t('error.MANDATORY_CRITERIA') }}</span>
              </p>
            </div>
          </div>

          <div class="row mb-2">
            <!-- Start Date Traitement -->
            <div class="col-12 col-sm-6 col-xl-3">
              <label class="control-label fw-bold mb-1">
                {{ $t('label.clearing.selectedDateTraitementBegin') }}
              </label>
              <input
                v-model="criterias.selectedDateTraitementBegin"
                :class="status(v$.criterias.selectedDateTraitementBegin)"
                type="date"
                class="form-control rounded-0"
                placeholder="yyyy-MM-dd"
              >
              <p
                v-if="validationError"
                class="error"
              >
                <span v-if="v$.criterias.selectedDateTraitementBegin.required.$invalid">{{ $t('error.COMPENSATION_DATE_MANDATORY_CRITERIA') }}</span>
                <span v-else-if="v$.criterias.selectedDateTraitementBegin.rangeMax.$invalid">{{ $t('error.COMPENSATION_DATE_TRAITEMENT_RANGE') }}</span>
              </p>
            </div>

            <!-- End Date Traitement -->
            <div class="col-12 col-sm-6 col-xl-3">
              <label class="control-label fw-bold mb-1">
                {{ $t('label.clearing.selectedDateTraitementEnd') }}
              </label>
              <input
                v-model="criterias.selectedDateTraitementEnd"
                :class="status(v$.criterias.selectedDateTraitementEnd)"
                type="date"
                class="form-control rounded-0"
                placeholder="yyyy-MM-dd"
              >
              <p
                v-if="validationError"
                class="error"
              >
                <span v-if="v$.criterias.selectedDateTraitementEnd.required.$invalid">{{ $t('error.MANDATORY_CRITERIA') }}</span>
                <span v-else-if="v$.criterias.selectedDateTraitementEnd.rangeMax.$invalid">{{ $t('error.COMPENSATION_DATE_TRAITEMENT_RANGE') }}</span>
              </p>
            </div>

            <!-- Amount -->
            <div class="col-12 col-sm-6 col-xl-3">
              <label class="control-label fw-bold mb-1">
                {{ $t('label.authorization.amount') }}
              </label>
              <span
                :title="$t('label.authorization.amountFormat')"
                data-bs-toggle="tooltip"
                data-bs-placement="left"
                class="label label-default ms-2"
              >
                <i class="fa fa-question-circle fa-xs" />
              </span>
              <input
                v-model="criterias.selectedTransactionAmount"
                :class="status(v$.criterias.selectedTransactionAmount)"
                type="text"
                class="form-control rounded-0"
                :placeholder="$t('label.authorization.amount')"
              >
              <p
                v-if="validationError"
                class="error"
              >
                <span v-if="v$.criterias.selectedTransactionAmount.$invalid">{{ $t('error.AUTHORIZATION_AMOUNT_FORMAT') }}</span>
              </p>
            </div>
          </div>

          <div class="row mb-2">
            <div class="col justify-content-end text-end criteria-button">
              <button
                type="button"
                class="btn btn-default btn-reset rounded-pill font-custom me-2"
                @click="reset()"
              >
                {{ $t('button.reset') }}
              </button>
              <button
                class="btn btn-primary btn-submit rounded-pill font-custom text-uppercase font-inverted"
                type="button"
                @click="search()"
              >
                <i class="fa fa-search me-1" />
                {{ $t('button.search') }}
              </button>
            </div>
          </div>
        </form>
      </div>

      <div v-if="$store.state.clearings.clearings.length">
        <div class="card card-body bg-light mb-4">
          <button
            type="button"
            class="btn btn-primary rounded-pill font-custom font-inverted btn-export"
            @click="downloadCSV()"
          >
            <i class="far fa-file-excel me-1" />
            {{ $t('button.export') }}
          </button>
        </div>

        <div class="table-responsive">
          <table
            class="table table-hover table-sm table-striped border"
            style="white-space:nowrap;"
          >
            <thead>
              <tr>
                <th>{{ $t('label.clearing.distributorRef') }}</th>
                <th>{{ $t('label.clearing.cardSchemeCode') }}</th>
                <th>{{ $t('label.clearing.pan') }}</th>
                <th>{{ $t('label.clearing.transactionCategory') }}</th>
                <th>{{ $t('label.clearing.dateTransaction') }}</th>
                <th>{{ $t('label.clearing.processingDate') }}</th>
                <th>{{ $t('label.clearing.transactionAmount') }}</th>
                <th>{{ $t('label.clearing.reversalFlag') }}</th>
                <!-- th>{{ $t('label.clearing.offlineAutReversalFlag') }}</th -->
                <th>{{ $t('label.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="clearing in displayedCompensations"
                :key="clearing.id"
                class="mb-3"
              >
                <td>{{ clearing.distributorRef }}</td>
                <td>{{ clearing.cardSchemeCode }}</td>
                <td>{{ clearing.pan }}</td>
                <td>{{ clearing.classeTransaction }}</td>
                <td>{{ $filters.date(clearing.dateTransaction) }}</td>
                <td>{{ $filters.date(clearing.processingDate) }}</td>
                <td>{{ $filters.amountObject(clearing.transactionAmount) }}</td>
                <td>{{ $filters.boolean(clearing.reversalFlag) }}</td>
                <!-- td>{{ $filters.boolean(clearing.offlineAutReversalFlag) }}</td -->
                <td>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary border-secondary rounded-pill font-custom"
                    @click="showDetails(clearing)"
                  >
                    {{ $t('button.detail') }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <Pager
          :posts="$store.state.clearings.clearings"
          @page-update="displayedCompensations = $event;"
        />
      </div>

      <div
        v-if="isProcessing"
        class="text-center"
      >
        <img
          src="@/assets/img/loader.gif"
          class="center-block lazyload"
        >
      </div>

      <div
        v-else-if="error"
        class="alert alert-warning text-center border-0 shadow-sm rounded-0"
      >
        {{ $t('error.'+`${responseErr}`) }}
      </div>
    </div>
  </div>
</template>

<script src="./ClearingList.js"></script>

<style src="./ClearingList.scss" scoped lang="scss"></style>
